import { Radio } from 'antd';
import { ReactComponent as CloseIcon } from 'app/assets/img/close_icon.svg';
import StyledButton from 'app/components/StyledButton';
import ModalContainer from 'app/components/ModalContainer';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectTranslations } from '../App/slice/selectors';
import ReadMore from './ReadMore';
import { FHT_FALLBACK_IMAGE } from './drawerOtherFHT.helpers';

export type SunWorldTicketItem = {
  id?: string | number;
  name?: string;
  description?: string;
  imageUrl?: string | null;
  siteName?: string;
  location?: string;
  parkName?: string;
  otaProductCode?: string;
  otaSiteName?: string;
  media?: string[];
  paxAdditionalAmount?: number;
  additionalAmount?: number;
};

interface SunWorldTicketsModalProps {
  visible: boolean;
  onClose: () => void;
  tickets: SunWorldTicketItem[];
  mode?: 'info' | 'selection';
  selectedTicketId?: string | number;
  onSave?: (ticket: SunWorldTicketItem) => void;
}

export const getTicketLocation = (ticket: SunWorldTicketItem): string =>
  ticket.location ||
  ticket.siteName ||
  ticket.parkName ||
  ticket.otaSiteName ||
  '';

export const getTicketKey = (ticket: SunWorldTicketItem, index = 0): string =>
  String(ticket.id ?? ticket.otaProductCode ?? `${ticket.name}-${index}`);

const getTicketImage = (ticket: SunWorldTicketItem): string | null =>
  ticket.imageUrl || ticket.media?.[0] || null;

const SunWorldTicketsModal: React.FC<SunWorldTicketsModalProps> = ({
  visible,
  onClose,
  tickets,
  mode = 'info',
  selectedTicketId,
  onSave,
}) => {
  const translations = useSelector(selectTranslations);
  const isSelectionMode = mode === 'selection';
  const [selectedId, setSelectedId] = useState<string>('');

  const normalizedTickets = useMemo(
    () =>
      tickets.map((ticket, index) => ({
        ...ticket,
        key: getTicketKey(ticket, index),
        imageUrl: getTicketImage(ticket),
        siteName: ticket.siteName || ticket.otaSiteName,
      })),
    [tickets],
  );

  useEffect(() => {
    if (!visible) return;
    const initialTicket =
      normalizedTickets.find(
        t => getTicketKey(t) === String(selectedTicketId),
      ) || normalizedTickets[0];
    setSelectedId(initialTicket ? getTicketKey(initialTicket) : '');
  }, [visible, normalizedTickets, selectedTicketId]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = FHT_FALLBACK_IMAGE;
  };

  const handleSave = () => {
    const selectedTicket = normalizedTickets.find(
      ticket => getTicketKey(ticket) === selectedId,
    );
    if (selectedTicket) {
      onSave?.(selectedTicket);
    }
    onClose();
  };

  const renderInfoTicket = (
    ticket: SunWorldTicketItem & { key: string },
    index: number,
  ) => {
    const location = getTicketLocation(ticket);
    return (
      <TicketCard key={ticket.key ?? `${ticket.name}-${index}`}>
        <TicketThumb
          src={ticket.imageUrl || FHT_FALLBACK_IMAGE}
          alt={ticket.name}
          onError={handleImageError}
        />
        <TicketContent>
          <TicketName>{ticket.name}</TicketName>
          {location && (
            <TicketLocation>
              <i className="fa-solid fa-location-dot" />
              <span>{location}</span>
            </TicketLocation>
          )}
          {ticket.description && (
            <ReadMore
              lines={3}
              html={ticket.description}
              moreText="b2b.txt.expand"
              lessText="b2b.txt.collapse"
            />
          )}
        </TicketContent>
      </TicketCard>
    );
  };

  const renderSelectionTicket = (
    ticket: SunWorldTicketItem & { key: string },
    index: number,
  ) => {
    const location = getTicketLocation(ticket);
    const isSelected = selectedId === getTicketKey(ticket, index);

    return (
      <SelectionTicketCard
        key={ticket.key ?? `${ticket.name}-${index}`}
        type="button"
        onClick={() => setSelectedId(getTicketKey(ticket, index))}
        $selected={isSelected}
      >
        <TicketThumb
          src={ticket.imageUrl || FHT_FALLBACK_IMAGE}
          alt={ticket.name}
          onError={handleImageError}
        />
        <TicketContent>
          <TicketName>{ticket.name}</TicketName>
          {location && (
            <TicketLocation>
              <i className="fa-solid fa-location-dot" />
              <span>{location}</span>
            </TicketLocation>
          )}
          {ticket.description && (
            <ReadMore
              lines={2}
              html={ticket.description}
              moreText="b2b.txt.expand"
              lessText="b2b.txt.collapse"
            />
          )}
        </TicketContent>
        <SelectionRadioWrap onClick={e => e.stopPropagation()}>
          <StyledRadio
            checked={isSelected}
            onChange={() => setSelectedId(getTicketKey(ticket, index))}
          />
        </SelectionRadioWrap>
      </SelectionTicketCard>
    );
  };

  return (
    <ModalContainer
      isShow={visible}
      onCloseDialog={onClose}
      maskClosable
      closable
      modalPadding="0"
      modalHeight="initial"
      CloseIcon={<CloseIcon width={12} height={12} />}
      modalProps={{ destroyOnClose: true }}
      styleSlots={{
        iconWrapperStyle: {
          margin: 0,
        },
        modalContentStyle: {
          maxWidth: 980,
          width: '96vw',
          maxHeight: '80vh',
          height: 'auto',
          padding: 0,
          borderRadius: 16,
        },
        modalBodyStyle: {
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        modalStyle: {
          maxWidth: 'calc(100vw - 32px)',
        },
      }}
    >
      <ModalHeader>
        <ModalTitle>
          {isSelectionMode
            ? translations?.['b2b.txt.chooseSunWorldTicket'] ||
              'Lựa chọn vé Sun World'
            : translations?.['b2b.txt.sunWorldTicketInfo'] ||
              'Thông tin vé Sun World'}
        </ModalTitle>
      </ModalHeader>
      <ModalBody $hasFooter={isSelectionMode} $selectionMode={isSelectionMode}>
        {normalizedTickets.length === 0 ? (
          <EmptyText>
            {translations?.['b2b.txt.noData'] || 'Không có dữ liệu'}
          </EmptyText>
        ) : (
          normalizedTickets.map((ticket, index) =>
            isSelectionMode
              ? renderSelectionTicket(ticket, index)
              : renderInfoTicket(ticket, index),
          )
        )}
      </ModalBody>
      {isSelectionMode && (
        <ModalFooter>
          <StyledButton
            textColor="#fff"
            height="40px"
            style={{ minWidth: 88, borderRadius: 8 }}
            disabled={!selectedId}
            onClick={handleSave}
          >
            {translations?.['b2b.txt.save'] || 'Lưu'}
          </StyledButton>
        </ModalFooter>
      )}
    </ModalContainer>
  );
};

export default SunWorldTicketsModal;

const ModalHeader = styled.div`
  padding: 16px 48px 16px 20px;
  border-bottom: 1px solid #e5e5e5;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #000;
`;

const ModalBody = styled.div<{
  $hasFooter?: boolean;
  $selectionMode?: boolean;
}>`
  padding: ${({ $selectionMode }) => ($selectionMode ? '0' : '16px 20px 20px')};
  overflow-y: auto;
  flex: 1;
  max-height: ${({ $hasFooter }) =>
    $hasFooter ? 'calc(80vh - 120px)' : 'calc(80vh - 60px)'};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px 16px;
  border-top: 1px solid #e5e5e5;
  flex-shrink: 0;
`;

const TicketCard = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fff;

  & + & {
    margin-top: 12px;
  }
`;

const SelectionTicketCard = styled.button<{ $selected?: boolean }>`
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  border: none;
  border-bottom: 1px solid #e5e5e5;
  background: ${({ $selected }) => ($selected ? '#fff9f6' : '#fff')};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fff9f6;
  }
`;

const TicketThumb = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const TicketContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const TicketName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-bottom: 4px;
  line-height: 1.4;
`;

const TicketLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;

  i {
    font-size: 12px;
    color: #888;
  }
`;

const SelectionRadioWrap = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 4px;
  flex-shrink: 0;
`;

const StyledRadio = styled(Radio)`
  .ant-radio-inner {
    width: 18px;
    height: 18px;
    border-color: #ccc;
  }

  .ant-radio-checked .ant-radio-inner {
    border-color: #f06f24;
    background-color: #f06f24;
  }

  .ant-radio-checked .ant-radio-inner::after {
    background-color: #fff;
    transform: scale(0.45);
  }

  &:hover .ant-radio-inner {
    border-color: #f06f24;
  }
`;

const EmptyText = styled.p`
  margin: 0;
  text-align: center;
  color: #888;
  font-size: 14px;
  padding: 24px 20px;
`;
